import {ChangeDetectionStrategy, Component, computed, inject, input, output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {CustomerProductHistoryTimelineSummary, UserSelect} from '../../../../../api';
import {PartyAvatar} from '../../../../components/avatar/party-avatar.component';
import {
  buildHistoryChangeRows,
  EVENT_STYLES,
  EventStyle,
  HistoryChangeRow,
} from '../../../directory/components/customer-products/customer-product-history.utils';

@Component({
  selector: 'elcia-dashboard-product-history-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, TranslatePipe, PartyAvatar],
  templateUrl: './dashboard-product-history-item.html',
  host: {
    // Every entry names a customer that still exists, so the whole row is always an affordance.
    class: 'flex gap-3 px-3 py-2.5 rounded cursor-pointer transition-colors duration-200 motion-reduce:transition-none hover:bg-primary-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500',
    role: 'button',
    tabindex: '0',
    '(click)': 'onSelect.emit()',
    '(keydown.enter)': 'onSelect.emit()',
    '(keydown.space)': 'onSelect.emit(); $event.preventDefault()',
  },
})
export class DashboardProductHistoryItem {
  private readonly translate = inject(TranslateService);

  entry = input.required<CustomerProductHistoryTimelineSummary>();
  isLast = input.required<boolean>();
  onSelect = output<void>();

  private readonly langChange = toSignal(this.translate.onLangChange, {initialValue: null});

  private readonly style = computed<EventStyle>(() => EVENT_STYLES[this.entry().event]);

  protected readonly eventLabelKey = computed(() => this.style().labelKey);
  protected readonly iconClass = computed(() => this.style().icon);
  protected readonly markerClasses = computed(() => this.style().marker);

  protected readonly parentProductName = computed(() => this.entry().parentProductName);

  // Read by the tooltip, where the truncated header cannot show the whole chain.
  protected readonly entryLabel = computed(() => {
    const entry = this.entry();
    const productLabel = this.parentProductName()
      ? `${this.parentProductName()} › ${entry.productName}`
      : entry.productName;

    return `${entry.customerName} · ${productLabel}`;
  });

  protected readonly authors = computed<UserSelect[]>(() => {
    const author = this.entry().createdBy;
    return author ? [author] : [];
  });

  protected readonly changes = computed<HistoryChangeRow[]>(() => {
    this.langChange();
    return buildHistoryChangeRows(this.entry().changes, this.translate);
  });
}
